import { Add, DeleteOutline } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Grid2,
  IconButton,
  OutlinedInput,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InlineCopyButton from "../common/InlineCopyButton";
import { SwayyApiError } from "../../../api/SwayyProductsAPI";
import { SwayyOptionValue } from "../../../models/options/SwayyCatalogOption";
import { SwayyProductVariantsStore } from "../../../stores/SwayyProductVariantsStore";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";

interface SwayyProductVariantsPanelProps {
  productId: number;
  store: SwayyProductVariantsStore;
  onApiError: (error: SwayyApiError, title: string) => void;
  onSuccess: (title: string, message: string) => void;
  disabled?: boolean;
}

const primaryBtnSx = {
  minHeight: 30,
  px: 1.5,
  py: 0,
  fontSize: 12,
  color: swayyColors.white,
  backgroundColor: swayyColors.pine,
  "&:hover": {
    backgroundColor: swayyColors.pineHover,
    color: swayyColors.white,
  },
  "&.Mui-disabled": {
    color: swayyColors.white,
    opacity: 0.55,
  },
} as const;

const secondaryBtnSx = {
  minHeight: 30,
  px: 1.5,
  py: 0,
  fontSize: 12,
} as const;

const inputSx = {
  borderRadius: swayyRadii.md,
  fontFamily: swayyFont,
  fontSize: 13,
  height: 32,
  bgcolor: swayyColors.white,
  "& .MuiOutlinedInput-input": {
    py: 0.5,
    height: 32,
    boxSizing: "border-box",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: swayyColors.border },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
} as const;

const variantsThSx = {
  fontSize: "0.7rem",
  fontWeight: 600,
  color: swayyColors.textSecondary,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  whiteSpace: "nowrap" as const,
  py: 1.25,
  px: 1.5,
  borderBottom: `1px solid ${swayyColors.border}`,
  backgroundColor: swayyColors.pageBg,
  fontFamily: swayyFont,
} as const;

const variantsTdSx = {
  py: 1.25,
  px: 1.5,
  borderBottom: `1px solid ${swayyColors.border}`,
  verticalAlign: "middle" as const,
} as const;

const ValueChip = ({
  value,
  selected,
  onClick,
  disabled,
}: {
  value: SwayyOptionValue;
  selected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}) => (
  <Chip
    size="small"
    clickable={!!onClick && !disabled}
    onClick={onClick}
    disabled={disabled}
    label={
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
      >
        {value.hex ? (
          <Box
            component="span"
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: value.hex,
              border: `1px solid ${selected ? "rgba(255,255,255,0.5)" : swayyColors.border}`,
              display: "inline-block",
            }}
          />
        ) : null}
        {value.name}
      </Box>
    }
    variant={selected ? "filled" : "outlined"}
    sx={{
      height: 28,
      borderRadius: swayyRadii.full,
      borderColor: selected ? swayyColors.pine : swayyColors.border,
      backgroundColor: selected ? swayyColors.pine : swayyColors.white,
      color: selected ? swayyColors.white : swayyColors.deepGreen,
      fontFamily: swayyFont,
      fontSize: 12,
      fontWeight: 600,
      "& .MuiChip-label": {
        color: selected ? swayyColors.white : swayyColors.deepGreen,
      },
      "&:hover": onClick
        ? {
            backgroundColor: selected
              ? swayyColors.pineHover
              : swayyColors.pineLight,
            color: selected ? swayyColors.white : swayyColors.deepGreen,
            "& .MuiChip-label": {
              color: selected ? swayyColors.white : swayyColors.deepGreen,
            },
          }
        : undefined,
    }}
  />
);

const FieldLabel = ({ children }: { children: string }) => (
  <CustomTypography
    variant="body2"
    color={swayyColors.deepGreen}
    fontWeight={600}
    sx={{ mb: 0.75, fontFamily: swayyFont }}
  >
    {children}
  </CustomTypography>
);

const SwayyProductVariantsPanel = ({
  productId,
  store,
  onApiError,
  onSuccess,
  disabled = false,
}: SwayyProductVariantsPanelProps) => {
  const [addValueForOptionId, setAddValueForOptionId] = useState<number | null>(
    null,
  );
  const mutationDisabled = disabled || store.isUpdating;

  const addingOption =
    store.eligibleOptions.find((option) => option.id === addValueForOptionId) ||
    null;
  const isSwatch =
    String(addingOption?.displayType || "").toLowerCase() === "swatch";

  useEffect(() => {
    store.resetAddValueForm();
  }, [addValueForOptionId, store]);

  const closeAddValueForm = () => {
    setAddValueForOptionId(null);
    store.resetAddValueForm();
  };

  const notifyError = (error: SwayyApiError, title: string) => {
    onApiError(error, title);
  };

  const handleSaveVariants = async () => {
    if (disabled) return;
    try {
      const { error, result } = await store.saveAndCreateVariants(productId);
      if (error) {
        notifyError(
          error,
          error.message === "Nothing new to create."
            ? "Nothing new to create."
            : "Failed to create variants.",
        );
        return;
      }
      if (!result) return;
      if (result.failed.length > 0) {
        const detail = result.failed
          .map((f) => f.message || f.code)
          .filter(Boolean)
          .join("; ");
        if (result.createdCount > 0) {
          onSuccess(
            "Variants partially created",
            `${result.createdCount} created. Some failed: ${detail}`,
          );
        } else {
          notifyError(
            new SwayyApiError(detail || "Unable to create variants."),
            "Failed to create variants.",
          );
        }
        return;
      }
      onSuccess(
        "Variants created",
        `${result.createdCount} variant(s) created.`,
      );
      closeAddValueForm();
    } catch (error) {
      notifyError(
        error instanceof SwayyApiError
          ? error
          : new SwayyApiError("Unable to create variants."),
        "Failed to create variants.",
      );
    }
  };

  const handleCreateValue = async () => {
    if (disabled || addValueForOptionId == null) return;
    if (!store.validateAddValueForm(isSwatch)) return;
    try {
      const error = await store.createOptionValue(
        productId,
        addValueForOptionId,
        isSwatch,
      );
      if (error) {
        notifyError(error, "Failed to create value.");
        return;
      }
      closeAddValueForm();
      onSuccess("Value created", "The value was added to the option.");
    } catch (error) {
      notifyError(
        error instanceof SwayyApiError
          ? error
          : new SwayyApiError("Unable to create value."),
        "Failed to create value.",
      );
    }
  };

  const handleDeleteVariant = async (variantId: number) => {
    if (disabled) return;
    try {
      const error = await store.deleteVariant(productId, variantId);
      if (error) {
        notifyError(error, "Failed to delete variant.");
      }
    } catch (error) {
      notifyError(
        error instanceof SwayyApiError
          ? error
          : new SwayyApiError("Unable to delete variant."),
        "Failed to delete variant.",
      );
    }
  };

  const handleSaveVariantPrice = async (variantId: number) => {
    if (disabled) return;
    const error = await store.saveVariantPrice(variantId);
    if (error) {
      onApiError(error, "Failed to save price.");
      return;
    }
    onSuccess("Price saved", "B2C price updated for this variant.");
  };

  if (store.loadState === "loading" || store.loadState === "idle") {
    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: swayyRadii.lg,
          borderColor: swayyColors.border,
          backgroundColor: swayyColors.white,
        }}
      >
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={120} />
        </Stack>
      </Paper>
    );
  }

  if (store.loadState === "error") {
    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: swayyRadii.lg,
          borderColor: swayyColors.border,
          backgroundColor: swayyColors.white,
          textAlign: "center",
        }}
      >
        <CustomTypography
          variant="heading5"
          color={swayyColors.deepGreen}
          sx={{ fontWeight: 700, mb: 1 }}
        >
          Unable to load variants
        </CustomTypography>
        <CustomTypography
          variant="body1"
          color={swayyColors.textSecondary}
          sx={{ mb: 2 }}
        >
          {store.errorMessage || "Something went wrong. Please try again."}
        </CustomTypography>
        <AppButton
          label="Retry"
          variant="primary"
          onClick={() => {
            void store
              .loadVariantsTab(productId, { force: true })
              .then((error) => {
                if (error) notifyError(error, "Failed to load variants.");
              });
          }}
          customStyles={primaryBtnSx}
        />
      </Paper>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: swayyRadii.lg,
          borderColor: swayyColors.border,
          backgroundColor: swayyColors.white,
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Box>
                <CustomTypography
                  variant="heading5"
                  color={swayyColors.deepGreen}
                  sx={{ fontWeight: 700 }}
                >
                  Build variants
                </CustomTypography>
                <CustomTypography
                  variant="caption"
                  color={swayyColors.textSecondary}
                  sx={{ display: "block", mt: 0.5 }}
                >
                  Click options to include. Click values under each option. Save
                  to add new combinations.
                </CustomTypography>
              </Box>
              <AppButton
                label={
                  store.isCreatingVariants || store.isSavingOptions
                    ? "Saving…"
                    : "Save variants"
                }
                variant="primary"
                disabled={
                  mutationDisabled ||
                  !store.canBuildVariants ||
                  store.isCreatingVariants ||
                  store.isSavingOptions ||
                  store.isPreviewing
                }
                onClick={() => void handleSaveVariants()}
                startIcon={
                  store.isCreatingVariants || store.isSavingOptions ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : undefined
                }
                customStyles={primaryBtnSx}
              />
            </Stack>

            {store.eligibleOptions.length === 0 ? (
              <CustomTypography
                variant="body2"
                color={swayyColors.textSecondary}
              >
                No eligible options. Create options under Options, then link
                them on Categories.
              </CustomTypography>
            ) : (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {store.eligibleOptions.map((option) => {
                  const selected = store.draftSelectedOptionIds.includes(
                    option.id,
                  );
                  return (
                    <Chip
                      key={option.id}
                      clickable
                      onClick={() => {
                        if (addValueForOptionId === option.id) {
                          closeAddValueForm();
                        }
                        store.toggleOptionSelection(option.id);
                      }}
                      label={option.name}
                      sx={{
                        height: 30,
                        borderRadius: swayyRadii.full,
                        fontFamily: swayyFont,
                        fontWeight: 700,
                        fontSize: 12,
                        border: `1.5px solid ${
                          selected ? swayyColors.pine : swayyColors.border
                        }`,
                        backgroundColor: selected
                          ? swayyColors.pine
                          : swayyColors.white,
                        color: selected
                          ? swayyColors.white
                          : swayyColors.deepGreen,
                        "& .MuiChip-label": {
                          color: selected
                            ? swayyColors.white
                            : swayyColors.deepGreen,
                        },
                        "&:hover": {
                          backgroundColor: selected
                            ? swayyColors.pineHover
                            : swayyColors.pineLight,
                          color: selected
                            ? swayyColors.white
                            : swayyColors.deepGreen,
                          "& .MuiChip-label": {
                            color: selected
                              ? swayyColors.white
                              : swayyColors.deepGreen,
                          },
                        },
                      }}
                    />
                  );
                })}
              </Stack>
            )}

            <Stack
              spacing={2}
              sx={{ mt: store.workingOptions.length ? 2.5 : 0 }}
            >
              {store.workingOptions.map((option) => {
                const selectedValueIds =
                  store.selectedValueIdsByOptionId[option.id] ?? [];
                const addingHere = addValueForOptionId === option.id;
                const optionIsSwatch =
                  String(option.displayType || "").toLowerCase() === "swatch";
                return (
                  <Box
                    key={option.id}
                    sx={{
                      p: 2,
                      borderRadius: swayyRadii.md,
                      border: `1px solid ${swayyColors.border}`,
                      backgroundColor: alpha(swayyColors.pine, 0.03),
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mb: 1 }}
                    >
                      <CustomTypography
                        variant="body2"
                        color={swayyColors.deepGreen}
                        fontWeight={700}
                        sx={{ fontFamily: swayyFont }}
                      >
                        {option.name}
                      </CustomTypography>
                      <AppButton
                        label="Add value"
                        variant="secondary"
                        startIcon={<Add sx={{ fontSize: 16 }} />}
                        disabled={mutationDisabled || addingHere}
                        onClick={() => setAddValueForOptionId(option.id)}
                        customStyles={secondaryBtnSx}
                      />
                    </Stack>
                    {option.values.length === 0 && !addingHere ? (
                      <CustomTypography
                        variant="body2"
                        color={swayyColors.textSecondary}
                      >
                        No values yet. Use Add value.
                      </CustomTypography>
                    ) : option.values.length > 0 ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        {option.values.map((value) => {
                          const isSelected = selectedValueIds.includes(
                            value.id,
                          );
                          return (
                            <ValueChip
                              key={value.id}
                              value={value}
                              selected={isSelected}
                              disabled={mutationDisabled}
                              onClick={() => {
                                store.toggleOptionValue(option.id, value.id);
                                store.schedulePreview(productId);
                              }}
                            />
                          );
                        })}
                      </Stack>
                    ) : null}

                    {addingHere && (
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 2,
                          borderRadius: swayyRadii.md,
                          border: `1px dashed ${swayyColors.pine}`,
                          backgroundColor: alpha(swayyColors.pine, 0.04),
                        }}
                      >
                        <CustomTypography
                          variant="body2"
                          color={swayyColors.deepGreen}
                          fontWeight={700}
                          sx={{ mb: 1.5 }}
                        >
                          New value for {option.name}
                        </CustomTypography>
                        <Grid2 container spacing={1.5}>
                          <Grid2 size={{ xs: 12, sm: optionIsSwatch ? 4 : 5 }}>
                            <FieldLabel>Value *</FieldLabel>
                            <OutlinedInput
                              value={store.addValueForm.value}
                              onChange={(event) =>
                                store.updateAddValueForm(
                                  "value",
                                  event.target.value,
                                )
                              }
                              placeholder="Value"
                              fullWidth
                              disabled={mutationDisabled}
                              sx={inputSx}
                            />
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: optionIsSwatch ? 3 : 4 }}>
                            <FieldLabel>SKU code *</FieldLabel>
                            <OutlinedInput
                              value={store.addValueForm.skuCode}
                              onChange={(event) =>
                                store.updateAddValueForm(
                                  "skuCode",
                                  event.target.value,
                                )
                              }
                              placeholder="SKU code"
                              fullWidth
                              disabled={mutationDisabled}
                              sx={inputSx}
                            />
                          </Grid2>
                          {optionIsSwatch && (
                            <Grid2 size={{ xs: 12, sm: 5 }}>
                              <FieldLabel>Hex</FieldLabel>
                              <OutlinedInput
                                value={store.addValueForm.hex}
                                onChange={(event) =>
                                  store.updateAddValueForm(
                                    "hex",
                                    event.target.value,
                                  )
                                }
                                placeholder="#RRGGBB"
                                fullWidth
                                disabled={mutationDisabled}
                                sx={inputSx}
                              />
                            </Grid2>
                          )}
                        </Grid2>
                        {store.addValueFormError ? (
                          <CustomTypography
                            variant="caption"
                            color={swayyColors.danger}
                            sx={{ display: "block", mt: 1 }}
                          >
                            {store.addValueFormError}
                          </CustomTypography>
                        ) : null}
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                          <AppButton
                            label="Cancel"
                            variant="secondary"
                            disabled={mutationDisabled}
                            onClick={closeAddValueForm}
                            customStyles={secondaryBtnSx}
                          />
                          <AppButton
                            label={store.isUpdating ? "Adding…" : "Add value"}
                            variant="primary"
                            disabled={mutationDisabled}
                            onClick={() => void handleCreateValue()}
                            customStyles={primaryBtnSx}
                          />
                        </Stack>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>

            {store.displayComboRows.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {store.displayComboRows.slice(0, 24).map((row, idx) => (
                    <Chip
                      key={`${row.label}-${idx}`}
                      size="small"
                      label={row.label}
                      variant="outlined"
                      sx={{
                        borderRadius: swayyRadii.full,
                        borderColor: row.exists
                          ? swayyColors.border
                          : swayyColors.pine,
                        color: swayyColors.deepGreen,
                        fontFamily: swayyFont,
                        opacity: row.exists ? 0.6 : 1,
                      }}
                    />
                  ))}
                  {store.displayComboRows.length > 24 ? (
                    <CustomTypography
                      variant="caption"
                      color={swayyColors.textSecondary}
                    >
                      +{store.displayComboRows.length - 24} more
                    </CustomTypography>
                  ) : null}
                </Stack>
              </Box>
            )}
          </Box>

          <Box>
            <CustomTypography
              variant="heading5"
              color={swayyColors.deepGreen}
              sx={{ fontWeight: 700, mb: 1.5 }}
            >
              Variants
            </CustomTypography>
            {store.variants.length === 0 ? (
              <Box
                sx={{
                  py: 4,
                  px: 2,
                  textAlign: "center",
                  borderRadius: swayyRadii.md,
                  border: `1px dashed ${swayyColors.border}`,
                  backgroundColor: swayyColors.pageBg,
                }}
              >
                <CustomTypography
                  variant="body1"
                  color={swayyColors.textSecondary}
                >
                  No variants yet.
                </CustomTypography>
              </Box>
            ) : (
              <>
                {store.pricingCaption ? (
                  <CustomTypography
                    variant="caption"
                    color={swayyColors.warning}
                    sx={{ display: "block", mb: 1.25 }}
                  >
                    {store.pricingCaption}
                  </CustomTypography>
                ) : null}
                <TableContainer
                  sx={{
                    overflowX: "auto",
                    borderRadius: swayyRadii.md,
                    border: `1px solid ${swayyColors.border}`,
                    backgroundColor: swayyColors.white,
                  }}
                >
                  <Table
                    size="small"
                    sx={{ minWidth: 640 }}
                    aria-label="Product variants pricing"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={variantsThSx}>Variant</TableCell>
                        <TableCell sx={variantsThSx}>SKU</TableCell>
                        <TableCell sx={variantsThSx}>MRP</TableCell>
                        <TableCell sx={variantsThSx}>Sale price</TableCell>
                        <TableCell sx={{ ...variantsThSx, width: 160 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {store.variants.map((variant) => {
                        const draft = store.priceDraftByVariantId[
                          variant.id
                        ] ?? {
                          mrp: "",
                          salePrice: "",
                        };
                        const savingPrice =
                          store.savingPriceVariantId === variant.id;
                        const priceDisabled =
                          mutationDisabled ||
                          savingPrice ||
                          store.b2cPriceListId == null;
                        return (
                          <TableRow key={variant.id} hover>
                            <TableCell sx={variantsTdSx}>
                              <CustomTypography
                                variant="body2"
                                color={swayyColors.deepGreen}
                                fontWeight={700}
                                sx={{
                                  maxWidth: 160,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {variant.name}
                              </CustomTypography>
                            </TableCell>
                            <TableCell sx={variantsTdSx}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <CustomTypography
                                  variant="caption"
                                  color={swayyColors.textSecondary}
                                  sx={{ fontFamily: "monospace" }}
                                >
                                  {variant.sku}
                                </CustomTypography>
                                {variant.sku ? (
                                  <InlineCopyButton
                                    value={variant.sku}
                                    title="Copy SKU"
                                    ariaLabel="Copy variant SKU"
                                  />
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ ...variantsTdSx, minWidth: 120 }}>
                              <OutlinedInput
                                size="small"
                                type="number"
                                value={draft.mrp}
                                disabled={priceDisabled}
                                onChange={(event) =>
                                  store.updatePriceDraft(
                                    variant.id,
                                    "mrp",
                                    event.target.value,
                                  )
                                }
                                inputProps={{
                                  min: 0,
                                  step: "0.01",
                                  "aria-label": `MRP for ${variant.name}`,
                                }}
                                sx={{
                                  ...inputSx,
                                  width: "100%",
                                  maxWidth: 140,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ ...variantsTdSx, minWidth: 120 }}>
                              <OutlinedInput
                                size="small"
                                type="number"
                                value={draft.salePrice}
                                disabled={priceDisabled}
                                onChange={(event) =>
                                  store.updatePriceDraft(
                                    variant.id,
                                    "salePrice",
                                    event.target.value,
                                  )
                                }
                                inputProps={{
                                  min: 0,
                                  step: "0.01",
                                  "aria-label": `Sale price for ${variant.name}`,
                                }}
                                sx={{
                                  ...inputSx,
                                  width: "100%",
                                  maxWidth: 140,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={variantsTdSx}>
                              <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="center"
                              >
                                <AppButton
                                  label={savingPrice ? "Saving…" : "Save"}
                                  variant="primary"
                                  disabled={priceDisabled}
                                  onClick={() =>
                                    void handleSaveVariantPrice(variant.id)
                                  }
                                  customStyles={{
                                    ...primaryBtnSx,
                                    minWidth: 72,
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  disabled={mutationDisabled || savingPrice}
                                  aria-label="Delete variant"
                                  title="Delete variant"
                                  onClick={() =>
                                    void handleDeleteVariant(variant.id)
                                  }
                                  sx={{
                                    p: 0.5,
                                    color: swayyColors.textSecondary,
                                    "&:hover": {
                                      color: swayyColors.danger,
                                      backgroundColor: alpha(
                                        swayyColors.danger,
                                        0.08,
                                      ),
                                    },
                                  }}
                                >
                                  <DeleteOutline sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </Stack>
      </Paper>
    </>
  );
};

export default observer(SwayyProductVariantsPanel);
